import { withoutLeadingSlash } from 'ufo';
import { Common } from '~/models/common';
import { Resolver } from '~/models/resolver';

export const resolverService = {
	fetch: async (): Promise<Resolver.Model[]> => {
		const params = {
			filter: {
				status: Common.Status.PUBLISHED,
			},
			fields: [
				'*',
				'form.*',
				'seo.label',
				'seo.title',
				'seo.header',
				'seo.keywords',
				'seo.description',
				'collectionItem.*',
			],
		};

		try {
			const response = await $fetch<Resolver.Model[]>(Resolver.api, {
				method: 'POST',
				body: { params },
			});

			return response;
		} catch (error) {
			console.error('Failed to fetch Resolver:', error);

			return [];
		}
	},

	resolve: async (url: string): Promise<Resolver.Model | undefined> => {
		const { clearBreadcrumbs, addBreadcrumbs } = useBreadcrumbs(url);
		clearBreadcrumbs();

		const resolvedPageUrlPart = url === '/' ? [''] : withoutLeadingSlash(url).split('/');
		const resolvedPageUrl = `/${resolvedPageUrlPart[0]}`;

		const params = {
			filter: {
				url: resolvedPageUrl,
				status: Common.Status.PUBLISHED,
				...(resolvedPageUrlPart.length > 1 ? { isSingleton: false } : {}),
			},
			fields: [
				'*',
				'form.*',
				'seo.label',
				'seo.title',
				'seo.header',
				'seo.keywords',
				'seo.description',
				'collectionItem.*',
			],
		};
		const [resolvedPage] = await $fetch<Resolver.Model[]>(Resolver.api, {
			method: 'POST',
			body: { params },
		});

		if (!resolvedPage) return undefined;

		if (resolvedPageUrlPart.length === 1) {
			if (resolvedPage.seo) {
				addBreadcrumbs(resolvedPageUrl, resolvedPage.seo.label);
			}
			return {
				...resolvedPage,
				isSubPage: false,
				alias: String(resolvedPageUrlPart[0]),
			};
		} else if (resolvedPageUrlPart.length === 2) {
			if (resolvedPage.seo) {
				addBreadcrumbs(resolvedPage.url, resolvedPage.seo.label);
			}

			const params = {
				filter: {
					alias: resolvedPageUrlPart[1],
					status: Common.Status.PUBLISHED,
				},
				fields: [
					'*',
					'form.*',
					'seo.label',
					'seo.title',
					'seo.header',
					'seo.keywords',
					'seo.description',
					'collectionItem.*',
				],
			};
			const [resolvedCollection] = await $fetch<Resolver.Model[]>(Resolver.api, {
				method: 'POST',
				body: { collection: resolvedPage.collection, params },
			});

			if (!resolvedCollection) return undefined;

			const resolvedCollectionUrl = `/${resolvedPageUrlPart[0]}/${resolvedPageUrlPart[1]}`;
			if (resolvedCollection.seo) {
				addBreadcrumbs(resolvedCollectionUrl, resolvedCollection.seo.label);
			}

			return {
				...resolvedPage,
				isSubPage: true,
				alias: String(resolvedPageUrlPart[1]),
				seo: resolvedCollection.seo,
			};
		} else {
			return undefined;
		}
	},

	resolveByCollection: async (collectionName: string): Promise<Resolver.Model | undefined> => {
		const params = {
			filter: {
				collection: collectionName,
				status: Common.Status.PUBLISHED,
			},
			fields: [
				'*',
				'form.*',
				'seo.label',
				'seo.title',
				'seo.header',
				'seo.keywords',
				'seo.description',
			],
		};

		try {
			const [resolvedPage] = await $fetch<Resolver.Model[]>(Resolver.api, {
				method: 'POST',
				body: { params },
			});

			return resolvedPage;
		} catch (error) {
			console.error('Failed to fetch Resolver:', error);

			return undefined;
		}
	},
};
